import { VersionInfo } from '@framework/ui/components/layout/footer/version-info/VersionInfo.tsx';
import { PageLayoutBase } from '@framework/ui/components/layout/PageLayoutBase.tsx';
import {
  type ISkipLink,
  SkipLinks,
} from '@framework/ui/components/skip-links/SkipLinks.tsx';
import { Breadcrumbs } from '@framework/ui/core/router/breadcrumbs/Breadcrumbs.tsx';
import clx from 'classnames';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { down } from 'styled-breakpoints';
import styled from 'styled-components';
import {
  FrontBreadcrumbsContainer,
  FrontFooterLayout,
  FrontTopPage,
} from '#theme';
import { PageLayoutHeader } from './header/PageLayoutHeader.tsx';

const PageContentWrapper = styled.div`
  padding: 12px 24px;
  padding-bottom: 24px;
  ${down('sm')} {
    padding: 12px 0;
  }
`;
const BreadcrumbsWrapper = styled.div`
  padding-bottom: 12px;
  max-width: 100%;
  ${down('sm')} {
    padding-left: 12px;
  }
`;

export interface PageLayoutProps {
  children: React.ReactNode;
  hasTopBar?: boolean;
  fixedHeader?: boolean;
  boxedContent?: boolean;
  /** Il content è una tabella */
  isTablePage?: boolean;
  noPadding?: boolean;
  className?: string;

  boxedHeader?: boolean;
  /** Pulsanti extra da mostrare in Header */
  extraButtons?: React.ReactNode;
  /** Lista di link aggiuntivi per accessibilità (skip links) */
  skipLinksAddiction?: ISkipLink[];
}

/**
 * Layout principale dell'applicativo.
 */
export function PageLayout(props: PageLayoutProps) {
  const { t } = useTranslation();
  const {
    hasTopBar = true,
    boxedHeader = true,
    boxedContent = true,
    noPadding = true,
    isTablePage = false,
    extraButtons,
    skipLinksAddiction,
  } = props;

  const footerLink = hasTopBar
    ? [
        {
          label: t('layout.skip-links.vai-al-footer', 'Vai al footer'),
          htmlId: 'main-footer',
        },
      ]
    : [];
  const skipLinks = [
    {
      label: t(
        'layout.skip-links.vai-al-menu-principale',
        'Vai al menu principale',
      ),
      htmlId: 'main-menu',
    },
    {
      label: t(
        'layout.skip-links.vai-al-contenuto-principale',
        'Vai al contenuto principale',
      ),
      htmlId: 'main-content',
    },
    ...(skipLinksAddiction ?? []),
    ...footerLink,
  ];

  return (
    <PageLayoutBase
      topPage={<FrontTopPage />}
      staticFooter={hasTopBar ? <VersionInfo /> : null}
      footer={hasTopBar && <FrontFooterLayout />}
      topBarHeader={
        hasTopBar && (
          <>
            <SkipLinks links={skipLinks} />
            <PageLayoutHeader
              fixed={props.fixedHeader}
              boxed={boxedHeader}
              extra={extraButtons}
            />
          </>
        )
      }
      className={clx(props.className)}
      boxedContent={boxedContent}
      isTablePage={isTablePage}
      paddingInTablePageFullWidth={false}
      fixedHeader={props.fixedHeader}
      noPadding={noPadding}
      backTopProps={{
        'aria-label': t(
          'layout.footer.vai-all-inizio-pagina',
          'Vai all’inizio della pagina',
        ),
      }}
    >
      <PageContentWrapper>
        {hasTopBar && (
          <BreadcrumbsWrapper>
            <FrontBreadcrumbsContainer>
              <Breadcrumbs />
            </FrontBreadcrumbsContainer>
          </BreadcrumbsWrapper>
        )}
        <div>{props.children}</div>
      </PageContentWrapper>
    </PageLayoutBase>
  );
}
